import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Phone, Mail, Send, FileText, RefreshCw, MessageSquare } from "lucide-react";
import { API, api, authHeaders } from "../../lib/api";

const STATUSES = ["new", "contacted", "resolved"];
const BADGE = { new: "bg-amber-100 text-amber-800", contacted: "bg-sky-100 text-sky-800", resolved: "bg-emerald-100 text-emerald-800" };
const SMS_LABEL = { pending: "pending", sent: "handed to MSG91", failed: "failed", disabled: "off (no MSG91 keys yet)", invalid_number: "not an Indian mobile" };
const DETAILS = [["district", "District"], ["mandal", "Mandal"], ["village", "Village"], ["survey_no", "Survey no."], ["door_no", "Door no."], ["sro", "SRO"], ["document_no", "Doc no."], ["document_year", "Doc year"]];

export const ECRequestsPanel = ({ handleErr }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${API}/ec-requests`, { headers: authHeaders() });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  }, [handleErr]);

  useEffect(() => { load(); }, [load]);

  const replace = (data) => setItems((xs) => xs.map((x) => (x.id === data?.id ? data : x)));

  const setStatus = async (id, status) => {
    try { replace((await api.patch(`${API}/ec-requests/${id}`, { status }, { headers: authHeaders() })).data); toast.success(`Marked as ${status}`); }
    catch (err) { handleErr(err); }
  };

  const resend = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`${API}/ec-requests/${id}/resend-alert`, {}, { headers: authHeaders() });
      replace(data);
      if (data?.email_status === "sent") toast.success("Alert email sent");
      else toast.error("Alert still failed. Check that your email domain is set up.");
    } catch (err) { handleErr(err); } finally { setBusy(null); }
  };

  const resendSms = async (id) => {
    setBusy(`sms-${id}`);
    try {
      const { data } = await api.post(`${API}/ec-requests/${id}/resend-sms`, {}, { headers: authHeaders() });
      replace(data);
      if (data?.sms_status === "sent") toast.success("Reference SMS handed to MSG91");
      else toast.error(`SMS not sent: ${SMS_LABEL[data?.sms_status] ?? data?.sms_status}`);
    } catch (err) { handleErr(err); } finally { setBusy(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this EC request?")) return;
    try { await api.delete(`${API}/ec-requests/${id}`, { headers: authHeaders() }); setItems((xs) => xs.filter((x) => x.id !== id)); toast.success("EC request deleted"); }
    catch (err) { handleErr(err); }
  };

  const openFile = async (rid, f) => {
    const win = window.open("", "_blank");
    try {
      const { data } = await api.get(`${API}/ec-requests/${rid}/files/${f.id}`, { headers: authHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: f.content_type }));
      if (win) win.location.href = url; else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { if (win) win.close(); handleErr(err); }
  };

  return (
    <div data-testid="admin-ec-panel">
      <div className="flex items-center justify-between gap-4">
        <h1 className="font-display text-3xl text-[#0A192F]">EC requests <span className="text-[#8A6A2B]">({items.length})</span></h1>
        <button onClick={load} data-testid="admin-ec-refresh-btn" className="grid h-10 w-10 place-items-center rounded-full border border-[#0A192F]/20 text-[#0A192F] hover:bg-white" aria-label="Refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
      </div>
      <div className="mt-6 space-y-4">
        {!loading && items.length === 0 && <p className="rounded-2xl bg-white p-10 text-center text-[#5A6578]" data-testid="admin-ec-empty">No EC requests yet.</p>}
        {items.map((e) => (
          <article key={e.id} className="rounded-2xl bg-white border border-[#0A192F]/10 p-6" data-testid={`admin-ec-${e.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="font-display text-2xl text-[#0A192F]">{e.name}</h2>
                  {e.ref && <span className="font-mono text-xs tracking-wider text-[#0A192F] bg-[#F4EEE3] rounded-full px-3 py-1" data-testid={`admin-ec-ref-${e.id}`}>{e.ref}</span>}
                  <span className={`rounded-full px-3 py-1 text-xs font-medium ${BADGE[e.status] ?? ""}`}>{e.status}</span>
                  <span className="text-xs text-[#5A6578]">Email alert: {e.email_status}</span>
                  <span className="text-xs text-[#5A6578]" data-testid={`admin-ec-sms-status-${e.id}`}>SMS: {SMS_LABEL[e.sms_status] ?? e.sms_status}</span>
                  {e.sms_status === "failed" && (
                    <button onClick={() => resendSms(e.id)} disabled={busy === `sms-${e.id}`} data-testid={`admin-ec-resend-sms-${e.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#8A6A2B]/50 px-3 py-1 text-xs text-[#8A6A2B] hover:bg-[#8A6A2B] hover:text-white transition-colors disabled:opacity-60">
                      <MessageSquare className="h-3 w-3" /> {busy === `sms-${e.id}` ? "Sending…" : "Resend SMS"}
                    </button>
                  )}
                  {e.email_status === "failed" && (
                    <button onClick={() => resend(e.id)} disabled={busy === e.id} data-testid={`admin-ec-resend-${e.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#8A6A2B]/50 px-3 py-1 text-xs text-[#8A6A2B] hover:bg-[#8A6A2B] hover:text-white transition-colors disabled:opacity-60">
                      <Send className="h-3 w-3" /> {busy === e.id ? "Sending…" : "Resend alert"}
                    </button>
                  )}
                </div>
                <p className="mt-1 font-mono text-xs tracking-wider text-[#8A6A2B] uppercase">{e.purpose} · EC {e.period_from} → {e.period_to} · {new Date(e.created_at).toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-2">
                <select value={e.status} onChange={(ev) => setStatus(e.id, ev.target.value)} data-testid={`admin-ec-status-${e.id}`} className="rounded-full border border-[#0A192F]/20 bg-white px-3 py-2 text-sm text-[#0A192F]">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={() => remove(e.id)} data-testid={`admin-ec-delete-${e.id}`} className="grid h-9 w-9 place-items-center rounded-full text-red-700 hover:bg-red-50" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <dl className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3 text-sm">
              {DETAILS.map(([k, l]) => (
                <div key={k}><dt className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#5A6578]">{l}</dt><dd className="text-[#0A192F] break-words">{e[k] || "—"}</dd></div>
              ))}
            </dl>
            {e.notes && <p className="mt-4 text-[#2C3545] whitespace-pre-wrap">{e.notes}</p>}
            {e.files?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {e.files.map((f) => (
                  <button key={f.id} onClick={() => openFile(e.id, f)} data-testid={`admin-ec-file-${f.id}`} className="inline-flex items-center gap-2 rounded-full bg-[#F4EEE3] px-3 py-1.5 text-xs text-[#0A192F] hover:bg-[#EDE5D6]">
                    <FileText className="h-3.5 w-3.5 text-[#8A6A2B]" /> {f.filename}
                  </button>
                ))}
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-5 text-sm">
              <a href={`tel:${e.phone}`} className="inline-flex items-center gap-2 text-[#0A192F] hover:text-[#8A6A2B]"><Phone className="h-4 w-4" />{e.phone}</a>
              {e.email && <a href={`mailto:${e.email}`} className="inline-flex items-center gap-2 text-[#0A192F] hover:text-[#8A6A2B]"><Mail className="h-4 w-4" />{e.email}</a>}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};
